import _merge from 'lodash/merge'
import React from 'react'
import { btoa, jsonStringify } from '@ztechapps/common.utils'

export type UseReportUrlProps = {
  url?: string
  query?: Record<string, any>
  fileName?: string
  accessToken?: string
}

export type UseReportUrlResult = {
  refreshUrl: (options: any) => void
  reportUrl: string
}
export const useReportUrl = ({ url, query, fileName, accessToken }: UseReportUrlProps): UseReportUrlResult => {
  const [reportUrl, setReportUrl] = React.useState('')
  const queryAsString = jsonStringify(query)

  // NOTE: buildUrl's dep array names fileName/url/queryAsString even though its own body only reads
  // the destructured call-argument (`url`, `query`, `fileName` shadow the outer values of the same
  // name) plus `accessToken` from the closure. That over-inclusion is load-bearing, not an oversight:
  // it is how refreshUrl and the mount effect below — both keyed only on `buildUrl` — learn that
  // url/query/fileName changed. `query` itself is deliberately tracked via the pre-stringified
  // `queryAsString`, not the raw object, because `query` is an unstable object literal at several call
  // sites across the estate (order/admin/mobile report viewers) — depending on it directly would
  // re-run the mount effect below on every render and loop. A correct fix (e.g. reading queryAsString
  // everywhere `query` is needed, or requiring callers to memoize `query`) is a restructure touching
  // every consumer's behaviour, left for a follow-up rather than guessed here.
  const buildUrl = React.useCallback(
    ({ url, query, fileName, timeStamp = undefined }) => {
      const btoaResult = btoa({ accessToken, ...query, timeStamp })
      const _url = `${url}/${btoaResult}/${fileName}`
      setReportUrl(_url)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, fileName, url, queryAsString]
  )

  const refreshUrl = React.useCallback(
    (options) => {
      const timeStamp = new Date().getTime()
      const mergeArgs = _merge({ url, query, fileName, timeStamp }, options)
      buildUrl(mergeArgs)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the NOTE above buildUrl
    [buildUrl]
  )

  React.useEffect(() => {
    buildUrl({ url, query, fileName })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the NOTE above buildUrl
  }, [buildUrl])

  //TODO: Rename the refreshURL to refresh only
  return { refreshUrl, reportUrl }
}
